import dayjs from 'dayjs'
import { CircleAlert, Lock, LockOpen, PlaneTakeoff } from '@/components/icons'
import { cn } from '@/lib/utils'
import type { FlightLogLifecycle } from '@@api/flightLog.shared'

/**
 * Flight log status surfaces (Figma: Flight Log Status Card
 * 12495:68821 / 12495:68835, trip ring card 12423:14442, flight-card row
 * 12495:68907 / 12495:68922). Three shapes share the lifecycle model:
 * - FlightLogStatusCard — card with times + a Start/View pill
 * - FlightLogRingCard   — trip-level aggregate with a progress ring
 * - FlightLogCompactRow — status line embedded in trip flight cards
 *
 * FlightLogStatusCard is intentionally theme-INVERTED (designer request):
 * the Figma Dark comp (12495:68821 / 12495:68835, #262626 card) renders in
 * light mode, the light comp (12495:68503, white card) in dark mode. The
 * inversion lives in the `--flight-log-card-*` token group (index.css), so
 * no `dark:` variants are needed here. The ring card keeps normal theming.
 */

const themedCardClass =
  'rounded-2xl border border-flight-log-card-border bg-flight-log-card-surface p-3 shadow-[0px_1px_3.5px_rgba(0,0,0,0.05)]'

export const flightLogStatusLabels: Record<FlightLogLifecycle, string> = {
  notStarted: 'Not Started',
  awaitingLanding: 'Awaiting Landing',
  inProgress: 'In Progress',
  complete: 'Signed off',
}

export interface FlightLogCardRouteTimes {
  offBlock?: string | null
  onBlock?: string | null
  blockTime?: string | null
  flightTime?: string | null
}

export interface FlightLogCardPostFlight {
  name?: string | null
  signedAt?: string | Date | null
}

function TimeCell({ label, value }: { label: string; value?: string | null }) {
  return (
    <div className="flex min-w-0 flex-1 flex-col gap-1">
      <span className="text-[10px] font-semibold uppercase leading-4 text-flight-log-card-content-subtle">
        {label}
      </span>
      <span className="text-base font-semibold leading-6 text-flight-log-card-content">{value || '—'}</span>
    </div>
  )
}

interface FlightLogStatusCardProps {
  lifecycle: FlightLogLifecycle
  routeTimes?: FlightLogCardRouteTimes | null
  postFlight?: FlightLogCardPostFlight | null
  onAction?: () => void
  className?: string
}

export function FlightLogStatusCard({
  lifecycle,
  routeTimes,
  postFlight,
  onAction,
  className,
}: FlightLogStatusCardProps) {
  const started = lifecycle !== 'notStarted'

  // Header subtitle: only not-started legs have one (12495:68821); started
  // cards are title-only — On Block lives in the times grid (12495:68835).
  const subtitle = lifecycle === 'notStarted' ? 'Not started.' : null

  const statusLine =
    lifecycle === 'complete' ? (
      <>
        <span className="text-flight-log-card-positive">Signed off </span>
        <span className="text-flight-log-card-content-subtle">
          {postFlight?.name ? `• ${postFlight.name} ` : ''}
          {postFlight?.signedAt ? `• ${dayjs(postFlight.signedAt).format('D MMM HH:mm')}` : ''}
        </span>
      </>
    ) : lifecycle === 'awaitingLanding' ? (
      <>
        <span className="text-flight-log-card-info">In flight</span>
        <span className="text-flight-log-card-content-subtle"> • awaiting landing</span>
      </>
    ) : (
      <>
        <span className="text-flight-log-card-notice">In progress</span>
        <span className="text-flight-log-card-content-subtle"> • needs sign-off</span>
      </>
    )

  return (
    <button
      type="button"
      onClick={onAction}
      className={cn(themedCardClass, 'flex w-full flex-col gap-3 text-left active:opacity-90', className)}
    >
      <div className="flex w-full items-center gap-3">
        <PlaneTakeoff className="size-8 shrink-0 text-flight-log-card-icon" />
        <div className="flex min-w-0 flex-1 flex-col">
          <p className="text-sm font-bold leading-5 text-flight-log-card-content">Flight Log</p>
          {subtitle && (
            <p className="truncate text-xs font-semibold leading-4 text-flight-log-card-content-subtle">
              {subtitle}
            </p>
          )}
        </div>
        <span
          className={cn(
            'shrink-0 rounded-full px-3 py-2 text-xs font-semibold leading-4 text-flight-log-card-pill-content',
            started ? 'bg-flight-log-card-pill-bold' : 'bg-flight-log-card-pill-brand'
          )}
        >
          {started ? 'View' : 'Start'}
        </span>
      </div>

      {started && (
        <>
          <div className="flex w-full items-center gap-4">
            <TimeCell label="Off Block" value={routeTimes?.offBlock} />
            <TimeCell label="Flight Time" value={routeTimes?.flightTime} />
            <TimeCell label="On Block" value={routeTimes?.onBlock} />
            <TimeCell label="Block Time" value={routeTimes?.blockTime} />
          </div>
          <div className="h-px w-full bg-flight-log-card-divider" />
          <div className="flex items-center gap-2">
            {/* Lock mirrors FlightLogCompactRow: closed once signed off,
                open while the log is still editable. */}
            {lifecycle === 'complete' ? (
              <Lock className="size-4 shrink-0 text-flight-log-card-positive" />
            ) : (
              <LockOpen
                className={cn(
                  'size-4 shrink-0',
                  lifecycle === 'awaitingLanding'
                    ? 'text-flight-log-card-info'
                    : 'text-flight-log-card-notice'
                )}
              />
            )}
            <p className="text-xs font-semibold leading-4">{statusLine}</p>
          </div>
        </>
      )}
    </button>
  )
}

// Static progress ring — same circle math as CountdownRing, no timer.
const RING_SIZE = 49
const RING_R = 21
const RING_STROKE = 4
const RING_CIRCUMFERENCE = 2 * Math.PI * RING_R

interface FlightLogRingCardProps {
  completed: number
  total: number
  subtitle: string
  onClick?: () => void
  className?: string
}

export function FlightLogRingCard({
  completed,
  total,
  subtitle,
  onClick,
  className,
}: FlightLogRingCardProps) {
  const progress = total > 0 ? Math.min(1, completed / total) : 0
  const Root = onClick ? 'button' : 'div'
  return (
    <Root
      type={onClick ? 'button' : undefined}
      onClick={onClick}
      className={cn(
        'rounded-2xl border border-border-L1 bg-surface-L0 p-3 shadow-[0px_1px_3.5px_rgba(0,0,0,0.05)] dark:border-transparent dark:bg-[#262626]',
        'flex w-full items-center gap-3 text-left',
        onClick && 'active:opacity-90',
        className
      )}
    >
      <div className="relative shrink-0" style={{ width: RING_SIZE, height: RING_SIZE }}>
        <svg
          className="absolute inset-0"
          width={RING_SIZE}
          height={RING_SIZE}
          viewBox={`0 0 ${RING_SIZE} ${RING_SIZE}`}
          fill="none"
        >
          <circle
            cx={RING_SIZE / 2}
            cy={RING_SIZE / 2}
            r={RING_R}
            strokeWidth={RING_STROKE}
            className="stroke-content-primary-subtle-light dark:stroke-white/10"
          />
          {progress > 0 && (
            <circle
              cx={RING_SIZE / 2}
              cy={RING_SIZE / 2}
              r={RING_R}
              strokeWidth={RING_STROKE}
              strokeLinecap="round"
              strokeDasharray={RING_CIRCUMFERENCE}
              strokeDashoffset={RING_CIRCUMFERENCE * (1 - progress)}
              transform={`rotate(-90 ${RING_SIZE / 2} ${RING_SIZE / 2})`}
              className="stroke-content-brand-bold"
            />
          )}
        </svg>
        <div className="absolute inset-0 flex items-center justify-center">
          <span className="text-sm font-bold text-content-primary-bold dark:text-[#FAFAFA]">
            {completed}/{total}
          </span>
        </div>
      </div>
      <div className="flex min-w-0 flex-1 flex-col">
        <p className="text-sm font-bold leading-5 text-content-primary-bold dark:text-[#FAFAFA]">Flight Logs</p>
        <p className="truncate text-xs font-semibold leading-4 text-content-primary-subtle dark:text-[#737373]">{subtitle}</p>
      </div>
      <div className="flex h-[25px] w-[25px] shrink-0 items-center justify-center rounded-full bg-background-notice-subtle dark:bg-[rgba(254,252,232,0.1)]">
        <CircleAlert className="size-4 text-background-notice-bold dark:text-[#EAB308]" />
      </div>
    </Root>
  )
}

interface FlightLogCompactRowProps {
  lifecycle: FlightLogLifecycle
  /** Signed-off time for complete logs; the leg departure otherwise. */
  timestamp?: string | Date | null
  className?: string
}

const compactRowConfig: Record<
  FlightLogLifecycle,
  { icon: typeof Lock; tone: string }
> = {
  notStarted: { icon: LockOpen, tone: 'text-content-primary-subtle' },
  inProgress: { icon: LockOpen, tone: 'text-background-notice-bold' },
  awaitingLanding: { icon: LockOpen, tone: 'text-background-notice-bold' },
  complete: { icon: Lock, tone: 'text-content-positive-default' },
}

export function FlightLogCompactRow({ lifecycle, timestamp, className }: FlightLogCompactRowProps) {
  const { icon: Icon, tone } = compactRowConfig[lifecycle]
  return (
    <div className={cn('flex items-center justify-between gap-2', className)}>
      <span className="text-sm font-bold leading-5 text-content-primary-bold">Flight Log</span>
      <span className="flex min-w-0 items-center gap-1.5 text-xs font-semibold leading-4">
        <Icon className={cn('size-4 shrink-0', tone)} />
        <span className={tone}>{flightLogStatusLabels[lifecycle]}</span>
        {timestamp && (
          <span className="truncate text-content-primary-subtle">
            • {dayjs(timestamp).format('D MMM HH:mm')}
          </span>
        )}
      </span>
    </div>
  )
}
